
import { UPLOADSTATUS, ServerResponse } from "../types";

/**
 * ExtFile === "Extended File".
 * This object "extends" the File Object
 */
export declare type ExtFile = {
    /** 
     * An identifier for the extFile
     */
    id?: number | string | undefined;
    /**
     * The file object. Used mostly when user selects or drops files in the client sid.
     */
    file?: File;
    /**
     * The name of the file. Used mostly for displaying file data from server.
     */
    name?: string;
    /**
     * The type of the file. Used mostly for displaying file data from server.
     */
    type?: string;
    /**
     * The size of the file. Used mostly for displaying file data from server.
     */
    size?: number;
    /**
     * a flag that determines whether the file is "valid", "not valid" or it is "not validated".
     */
    valid?: boolean;
    /**
     * The list of errors when the file was validated
     */
    errors?: string[];
    /**
     * The current upload status. (e.g. "uploading")
     */
    uploadStatus?: UPLOADSTATUS | undefined;
    /**
     * A message that shows the result of the upload process
     */
    uploadMessage?: string;
    /**
     * A string representation or web url of the image
     * that will be set to the "src" prop of an <img/> If
     * given, the component will use this image source instead of
     * reading the image file.
     */
    imageUrl?: string;
    /**
     * The XMLHttpRequest object for performing uploads to a server
     */
    xhr?: XMLHttpRequest;
    /**
     * The current percentage of upload progress.
     * This value will have a higher priority over the upload progress value calculated inside the component.
     * @default undefined
     */
    progress?: number;
    /**
     * The additional data that will be sent to the server
     * when files are uploaded individually
     */
    extraUploadData?: Record<string, any>;
    /**
     * Any kind of extra data that could be needed.
     */
    extraData?: Record<string, Object>;
    /**
     * The upload response from server
     */
    serverResponse?: ServerResponse;
    /**
     * The url to be used to perform a GET request in order to download the file.
     * This action is triggered when download button is clicked or pressed.
     * In case onDownload prop is given
     */
    downloadUrl?: string;
    /**
     * A string representation or web url of the video
     * that will be set to the "src" prop of an <video/> tag
     * <video src={`${videoUrl}`} />
     */
    videoUrl?: string;
    /**
     * The url to be used to perform the upload request 
     */
    uploadUrl?: string;
}

/**
 * A class definition for ExtFile.
 * This class has the purpose to allow the creation of instances
 * of an ExtFile for performing complex operations that cannot be
 * accomplished just by using the ExtFile type.
 * For instance, it can help in changing the value of some attributes
 * across different scopes thanks to memory reference.
 */
export class ExtFileInstance {
    /**
     * An identifier for the extFile
     */
    public id?: number | string;
    /**
     * The file object. Used mostly when user selects or drops files in the client sid.
     */
    public file?: File;
    /**
     * The name of the file. Used mostly for displaying file data from server.
     */
    public name?: string;
    /**
     * The type of the file. Used mostly for displaying file data from server.
     */
    public type?: string;
    /**
     * The size of the file. Used mostly for displaying file data from server.
     */
    public size?: number;

    public imageUrl?: string;
    /**
     * A flag that determines whether the file is valid, not valid or it is not validated.
     */
    public valid?: boolean;
    /**
     * The list of errors when the file was validated
     */
    public errors?: string[];
    /**
     * A message that shows the result of the upload process
     */
    public uploadMessage?: string;
    /**
     * The current upload status. (e.g. "uploading")
     */
    public uploadStatus?: UPLOADSTATUS | undefined;
    /**
     * The current upload progress
     */
    public progress?: number;
    /**
     * The XMLHttpRequest object for performing uploads to a server
     */
    public xhr?: XMLHttpRequest;
    /**
     * Any kind of extra data that could be needed.
     */
    public extraData?: Record<string, Object>;
    /**
     * The additional data that will be sent to the server
     * when filesare uploaded individually
     */
    public extraUploadData?: Record<string, any>;
    /**
     * The upload response from server
     */
    public serverResponse?: ServerResponse;
    /**
     * Url to perform a GET request in order to download the file.
     * This  action is triggered when download button is clicked or pressed.
     * In case onDownload prop is given
     */
    public downloadUrl?: string;
    /**
     * Link, URI, FIle object or string representation of a video
     */
    public videoUrl?: string;
    /**
     * The url to be used to perform the upload request 
     */
    public uploadUrl?: string;

    constructor(extFile: ExtFile) {
        const {
            id,
            file,
            name,
            size,
            type,
            imageUrl,
            valid,
            errors,
            uploadMessage,
            uploadStatus,
            progress,
            xhr,
            extraData,
            extraUploadData,
            serverResponse,
            downloadUrl,
            videoUrl,
            uploadUrl
        } = extFile;

        this.id = id;
        this.file = file;
        this.name = name;
        this.size = size;
        this.type = type;
        this.imageUrl = imageUrl;

        this.valid = valid;
        this.errors = errors;
        this.uploadStatus = uploadStatus;
        this.uploadMessage = uploadMessage;
        this.progress = progress;
        this.xhr = xhr;

        this.extraData = extraData;
        this.extraUploadData = extraUploadData;
        this.serverResponse = serverResponse;

        this.downloadUrl = downloadUrl;
        this.videoUrl = videoUrl;
        this.uploadUrl = uploadUrl;
    }
    /**
     * method under construction
     */
    /*  private static kamuiFile() {
 
     }
    /**
     * Copies all non undefined attributes from ExtFileInstance to a new ExtFile object
     * @param extFileInstance the instance of ExtFile
     * @returns an ExtFile object
     */
    static toExtFile(extFileInstance: ExtFileInstance): ExtFile {
        //console.log("before toExtFile()", extFileInstance);

        let extFileClone: ExtFile = {}; // the new empty object
        const extFileInstanceKeys = Object.keys(extFileInstance) as [keyof ExtFile];
        const extFileInstanceValues = Object.values(extFileInstance) as Array<ExtFile[keyof ExtFile]>;
        // let's copy all user properties into it
        for (let i = 0; i < extFileInstanceValues.length; i++) {
            const currentValue: ExtFile[keyof ExtFile] = extFileInstanceValues[i];


            const currKey = extFileInstanceKeys[i];
            if (currentValue !== undefined) {
                extFileClone[currKey] = currentValue as ExtFile[keyof ExtFile] as never;
            }
        }
        //console.log("after toExtFile()", extFileClone);
        //console.log("current extFileClone keys",Object.keys( extFileClone).length);
        return extFileClone;
    }
    /**
     * Copies all non undefined attributes from ExtFileInstance to a new ExtFile object.
     * @returns an ExtFile object
     */
    toExtFile(): ExtFile {
        return ExtFileInstance.toExtFile(this);
    }

    /*   static mock = (id?: number): ExtFileInstance => {
          return new ExtFileInstance(
              extFileMock(id)
          );
      } */
    static hasValidUrl(extFile: ExtFile | ExtFileInstance): boolean {
        return extFile.uploadUrl && extFile.uploadUrl.length > 0;
    }
    static someValidUrl(extFile: ExtFile[] | ExtFileInstance[]): boolean {
        return extFile.some(ExtFileInstance.hasValidUrl);
    }
}


